import type { ReactNode } from "react";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { INTERCOM } from "@volca/ui/components/ui/empty-machine";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";

export const PackChatState = ({
  title,
  body,
  footnote,
  layout = "panel",
  error,
  loading,
  retrying = false,
  action,
}: {
  title?: string;
  body?: string;
  footnote?: ReactNode;
  layout?: "collection" | "panel" | "workspace";
  error?: unknown;
  loading?: boolean;
  retrying?: boolean;
  action?: ReactNode;
}) => {
  if (loading) {
    return (
      <Stack
        gap={4}
        align="center"
        justify="center"
        className={emptyStateVariants({
          layout: layout === "collection" ? "collection" : "panel",
        })}
      >
        <Spinner layout="centered" />
      </Stack>
    );
  }

  const stateTitle = title ?? "PackChat couldn't be loaded";
  const stateBody = body ?? "Try again. If it keeps failing, contact support.";

  if (error)
    return (
      <ErrorState
        title={stateTitle}
        description={stateBody}
        retrying={retrying}
        illustration={layout === "collection" ? "none" : "responsive"}
      >
        {action}
      </ErrorState>
    );

  if (layout !== "collection")
    return (
      <MachineEmptyState
        art={INTERCOM}
        layout={layout}
        title={stateTitle}
        description={stateBody}
        footnote={footnote}
      >
        {action}
      </MachineEmptyState>
    );

  return (
    <Empty
      data-machine-host
      className={emptyStateVariants({ layout: "collection" })}
    >
      <EmptyHeader>
        <EmptyTitle>{stateTitle}</EmptyTitle>
        <EmptyDescription>{stateBody}</EmptyDescription>
      </EmptyHeader>
      {action && <EmptyContent data-machine-trigger>{action}</EmptyContent>}
    </Empty>
  );
};
